import { EventSource } from "eventsource";
import { throttle } from "lodash-es";
import { storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";

import { useOrganizationManager } from "@/composables/organization-manager";
import {
    refreshOnCrossTabEntitySync,
    refreshOnExternalEntityChange,
    wouldClobberLocalChanges,
} from "@/composables/refresh-on-entity-change";
import { useStekManager } from "@/composables/stek-manager";
import config from "@/config";
import { Artefact, type BpmnArtefact } from "@/lib/artefacts";
import { getDescriptionArtefact } from "@/lib/artefacts/markdown";
import { api, type types } from "@/lib/openapi";
import {
    BodyStartProcessProcessMgtGenerateDefinitionXmlStartPost,
    StartProcessProcessMgtGenerateDefinitionXmlStartPostData,
} from "@/lib/openapi/gen";
// Use direct import since there is a circular dependency in @hey-api which
// causes problems when importing `schemas` from @/lib/openapi/index.
import { Body_start_process_process_mgt_generate_definition_xml_start_postSchema } from "@/lib/openapi/gen/schemas.gen";
import { followLink } from "@/lib/openapi/helpers";
import { useAuthenticationStore } from "@/stores/authentication-store";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { defineEntityStore } from "@/stores/helpers";
import { Process } from "@/stores/processes-store";
import { useReleaseStore } from "@/stores/release-store";
import { useSyncStore } from "@/stores/sync-store";

import {
    FILE_SYNC_THROTTLE_MS,
    discardDraftsAfterSync,
    useDraftManager,
} from "./draft-manager";
import { useEditManager } from "./edit-manager";
import { useEntityNameManager } from "./entity-name-manager";
import { registerFlush } from "./pending-flush";
import { clearStaleContentOnSwitch } from "./stale-while-revalidate";
import { isAbortError, useLatestRequest } from "./use-latest-request";

export type { Process } from "@/stores/processes-store";

export const PROMPT_MAX_LENGTH =
    Body_start_process_process_mgt_generate_definition_xml_start_postSchema
        .properties.prompt.maxLength;

// Uses the current route to determine what the 'process' should be and fetches
// the process description for you.
export const useProcessStore = defineEntityStore("process", () => {
    const router = useRouter();
    const { organization } = useOrganizationManager();
    const { stek } = useStekManager();
    const { getToken } = useAuthenticationStore();

    const toast = useToast();
    const process = ref<Process>();
    const description = ref<Artefact<"text/markdown">>();
    const definition = ref<BpmnArtefact | undefined>();
    const syncStore = useSyncStore();
    const entityId = computed(() => process.value?.identity);
    const descriptionDraft = useDraftManager({
        entityId,
        entityType: EntityType.PROCESS,
        attribute: "description",
        artefact: description,
    });
    const definitionDraft = useDraftManager({
        entityId,
        entityType: EntityType.PROCESS,
        attribute: "process_definition",
        artefact: definition,
    });
    discardDraftsAfterSync(
        entityId,
        [descriptionDraft, definitionDraft],
        EntityType.PROCESS
    );

    const { process: processInfo } = useCurrentEntityManager();
    const releaseStore = useReleaseStore();
    const revisionQuery = computed(() =>
        releaseStore.activeRevision
            ? new URLSearchParams({ revision: releaseStore.activeRevision })
            : undefined
    );

    const isFetchingProcess = ref(false);
    const isFetchingDescription = ref(false);
    const isFetchingDefinition = ref(false);
    const generatingProcesses = ref<Record<string, boolean>>({});
    const isGeneratingBpmn = computed(() => {
        // processInfo.value?.identity always reflects the current route's process.
        // Fall back to process.value?.identity only when entity store hasn't loaded yet.
        const id = processInfo.value?.identity ?? process.value?.identity;
        if (!id) return false;
        // Guard process.value?.is_generating against stale data from a different process.
        return (
            (!!process.value?.is_generating &&
                process.value?.identity === id) ||
            !!generatingProcesses.value[id]
        );
    });
    const isAnyGenerating = computed(() =>
        Object.values(generatingProcesses.value).some(Boolean)
    );
    // Latest-request guard: each runUpdateProcess gets its own AbortController +
    // token, so a stale run can neither abort the current one nor commit results.
    const { begin, isCurrent } = useLatestRequest();
    // Revision the currently-loaded process was fetched at. Lets the
    // activeRevision watcher skip a redundant refetch when releases resolve to
    // the same revision the identity watcher already loaded (the duplicate
    // get_process on page load), while still refetching on a real release switch.
    let loadedRevision: string | undefined;

    const {
        isModified: isNameModified,
        reset: resetName,
        persist: persistName,
    } = useEntityNameManager(process);

    let inFlightKey: string | null = null;
    let inFlightPromise: Promise<void> | null = null;

    async function updateProcess(force = false) {
        const processId = processInfo.value?.identity;

        // In case the same process is selected, return early.
        if (!force && process.value && process.value.identity === processId) {
            return;
        }

        // Coalesce concurrent calls for the same process + revision. Several
        // watchers fire within milliseconds on page load — the identity watch
        // below and the history store's immediate watch — and without this each
        // would abort the previous in-flight request and re-issue it
        // (get_process started, cancelled, restarted). Same-target callers now
        // join the running fetch instead of racing it.
        const requestKey = `${processId ?? ""}|${releaseStore.activeRevision ?? ""}`;
        if (inFlightPromise && inFlightKey === requestKey) {
            return inFlightPromise;
        }

        const run = runUpdateProcess(processId);
        inFlightKey = requestKey;
        inFlightPromise = run;
        try {
            await run;
        } finally {
            if (inFlightPromise === run) {
                inFlightPromise = null;
                inFlightKey = null;
            }
        }
    }

    async function runUpdateProcess(processId: string | undefined) {
        // Start a new request: aborts the previous one and gives us our own
        // signal + token. Reading these locals (never a shared, reassignable
        // ref) is what makes the fan-in of watchers safe — see useLatestRequest.
        const { token, signal } = begin();

        // If no process id is set
        if (!processId) {
            description.value = undefined;
            definition.value = undefined;
            process.value = undefined;
            return;
        }

        isFetchingProcess.value = true;
        // Stale-while-revalidate: keep the previously-loaded process + description
        // + definition (the rendered BPMN diagram) on screen while the *same*
        // process is refetched (a revision switch or a background sync), so it
        // doesn't blank the page or flash the spinner. The data overwrites below;
        // the sub-fetches always refetch for the current process (updateDefinition
        // sets an empty artefact when it has no diagram, so a stale diagram never
        // lingers). Switching to a *different* process is handled by
        // clearStaleContentOnSwitch, which blanks these refs so the old diagram
        // never shows on the new process's page.

        try {
            const { response, data } =
                await api.startProcessProcessMgtGetProcessStartPost({
                    body: {
                        process_id: `${processId}`,
                    },
                    signal,
                });

            // A newer request superseded us while fetching — commit nothing.
            if (!isCurrent(token)) {
                return;
            }

            if (!response.ok) {
                if (response.status === 404) {
                    if (!releaseStore.activeRevision) {
                        router.replace({
                            name: "not-found",
                            params: {
                                orgName: organization.value?.name,
                                stekName: stek.value?.name,
                            },
                            query: {
                                type: "process",
                                id: processId,
                            },
                        });
                    }
                    return;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Landing-time clobber guard: edits may have appeared while this
            // GET was in flight — the notification-time guard can't see them.
            // Only a same-process refetch is dropped; navigating to a
            // different process must always commit. A dropped commit
            // self-heals: the edit's own sync re-notifies and refetches.
            if (
                processId === process.value?.identity &&
                wouldClobberLocalChanges(externalRefresh)
            ) {
                return;
            }
            process.value = data;
            // The fetched row is the new name baseline. Without this, a
            // server-side rename surfacing on a same-process refetch would
            // read as a local edit (the name manager only re-baselines on
            // identity change) and get echoed back as a save.
            persistName();
            loadedRevision = releaseStore.activeRevision ?? undefined;

            // Sync generating state from backend — the backend is the source
            // of truth so we don't rely solely on SSE connection state.
            if (data?.is_generating) {
                generatingProcesses.value[processId] = true;
            } else if (generatingProcesses.value[processId]) {
                generatingProcesses.value[processId] = false;
            }

            // Description + definition load on the SAME signal/token, so an abort
            // or a newer request cleanly supersedes them too — and isFetching
            // stays true until the diagram is ready, not just the process row.
            if (process.value) {
                await Promise.all([
                    updateDescription(signal, token),
                    updateDefinition(signal, token),
                ]);
            }
        } catch (e) {
            if (isAbortError(e)) {
                return;
            }
            throw e;
        } finally {
            // Only the current request owns the page-level fetch flag; a stale
            // run must not flip it off while a newer one is still loading.
            if (isCurrent(token)) {
                isFetchingProcess.value = false;
            }
        }
    }
    // Drop the previous process's content (incl. the BPMN diagram) when the
    // route switches to a different process, so stale-while-revalidate never
    // renders the old definition on the new process's page.
    clearStaleContentOnSwitch(
        () => processInfo.value?.identity,
        [process, description, definition]
    );
    watch(
        () => processInfo.value?.identity,
        () => updateProcess(true),
        // ``immediate`` so a fresh deep-link that creates this (singleton) store
        // *after* the parent-process identity has already resolved — e.g. landing
        // straight on a task page (ProcessElementPage), where ``processInfo`` is
        // the task's ancestor process — still loads the process + definition.
        // Without it the BPMN only appeared after visiting the process page first
        // (which populated this store). Matches the sibling element/lane stores.
        { immediate: true }
    );
    watch(
        () => releaseStore.activeRevision,
        (rev) => {
            // Only refetch on a genuine revision change; skip when releases
            // resolve to the revision the identity watcher already loaded.
            if (process.value && (rev ?? undefined) !== loadedRevision) {
                updateProcess(true);
            }
        }
    );

    /**
     * Lightweight backend check for is_generating — only updates the
     * generatingProcesses flag without resetting process/definition state.
     * Used by the polling watcher to avoid disrupting the page during generation.
     */
    const refreshGeneratingState = async () => {
        const processId = processInfo.value?.identity;
        if (!processId) return;
        try {
            const { response, data } =
                await api.startProcessProcessMgtGetProcessStartPost({
                    body: {
                        process_id: `${processId}`,
                    },
                });
            if (!response.ok || !data) return;
            if (data.is_generating) {
                generatingProcesses.value[processId] = true;
            } else if (generatingProcesses.value[processId]) {
                generatingProcesses.value[processId] = false;
            }
            // Keep process.value.is_generating in sync so the computed reflects
            // the latest backend state without a full page re-fetch.
            if (process.value?.identity === processId) {
                process.value.is_generating = data.is_generating ?? false;
            }
        } catch {
            // Swallow errors — polling should not disrupt the user.
        }
    };

    // Poll the backend every 3 seconds while generation is in progress so that
    // the generating state stays accurate after SSE reconnections or navigation.
    // A single interval is shared — the null guard prevents multiple concurrent
    // polling loops from accumulating across navigations or flag restores.
    let pollInterval: ReturnType<typeof setInterval> | null = null;
    watch(
        isGeneratingBpmn,
        (generating) => {
            if (generating && !pollInterval) {
                pollInterval = setInterval(async () => {
                    if (!isGeneratingBpmn.value) {
                        clearInterval(pollInterval!);
                        pollInterval = null;
                        return;
                    }
                    await refreshGeneratingState();
                }, 3000);
            } else if (!generating && pollInterval) {
                clearInterval(pollInterval);
                pollInterval = null;
            }
        },
        { immediate: true }
    );

    const updateDescription = async (signal: AbortSignal, token: number) => {
        if (process.value) {
            isFetchingDescription.value = true;
            try {
                const descriptionResult = await getDescriptionArtefact(
                    process.value,
                    "processDescription",
                    signal,
                    revisionQuery.value
                );

                // A newer request superseded us — commit nothing. Same for
                // edits that appeared while fetching (landing-time guard).
                if (
                    !isCurrent(token) ||
                    wouldClobberLocalChanges(externalRefresh)
                ) {
                    return;
                }

                description.value = descriptionResult;
            } catch (e) {
                if (isAbortError(e)) {
                    return;
                }
                throw e;
            } finally {
                if (isCurrent(token)) {
                    isFetchingDescription.value = false;
                }
            }
        }
    };

    const updateDefinition = async (signal: AbortSignal, token: number) => {
        if (process.value) {
            if (process.value.process_definition) {
                isFetchingDefinition.value = true;
                try {
                    const { response, data } = await followLink<File>(
                        process.value.process_definition,
                        "get",
                        undefined,
                        signal,
                        revisionQuery.value
                    );

                    // A newer request superseded us — commit nothing.
                    if (!isCurrent(token) || !process.value) {
                        return;
                    }

                    if (!response.ok) {
                        if (response.status === 404) {
                            return;
                        }
                        throw new Error(
                            `HTTP error! status: ${response.status}`
                        );
                    }

                    const definitionResult = await Artefact.createBpmnArtefact({
                        name: "processDefinition",
                        identity: process.value.process_definition.identity,
                        blob: data!,
                    });

                    // Re-check after the async artefact creation, and drop
                    // the commit if edits appeared while fetching
                    // (landing-time guard).
                    if (
                        !isCurrent(token) ||
                        !process.value ||
                        wouldClobberLocalChanges(externalRefresh)
                    ) {
                        return;
                    }

                    definitionResult.links =
                        process.value.process_definition.links;
                    definition.value = definitionResult;
                } catch (e) {
                    if (isAbortError(e)) {
                        return;
                    }
                    throw e;
                } finally {
                    if (isCurrent(token)) {
                        isFetchingDefinition.value = false;
                    }
                }
            } else {
                definition.value = Artefact.createEmptyArtefact({
                    mimetype: "application/bpmn+xml",
                    identity: process.value.identity + "_definition",
                });
            }
        }
    };

    /**
     * Generate a process definition as raw BPMN XML directly.
     */
    const generateProcessDefinitionXml = async (
        prompt: string,
        callbacks?: {
            onStarted?: () => void;
            onProgress?: () => void;
        }
    ): Promise<string | undefined> => {
        const targetProcessId = process.value!.identity;
        generatingProcesses.value[targetProcessId] = true;
        const token = await getToken();
        const body: BodyStartProcessProcessMgtGenerateDefinitionXmlStartPost = {
            prompt,
            process_id: targetProcessId,
        };
        // Set so that any change in the type will result in a type error.
        const url: StartProcessProcessMgtGenerateDefinitionXmlStartPostData["url"] =
            "/process/process_mgt/generate_definition_xml/start";
        const eventSource = new EventSource(`${config.bekendURI}${url}`, {
            fetch: (input, init) => {
                return fetch(input, {
                    ...init,
                    body: JSON.stringify(body),
                    method: "POST",
                    headers: {
                        ...init?.headers,
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                        Organization: organization?.value?.identity ?? "",
                    },
                });
            },
        });

        eventSource.addEventListener("open", () => {
            callbacks?.onStarted?.();
        });

        return await new Promise<string | undefined>((resolve, reject) => {
            eventSource.addEventListener("message", async ({ data }) => {
                try {
                    const parsedData = JSON.parse(data);
                    callbacks?.onProgress?.();
                    if (parsedData.status === "generating") {
                        callbacks?.onProgress?.();
                    } else if (parsedData.status === "completed") {
                        generatingProcesses.value[targetProcessId] = false;
                        resolve(parsedData.bpmn_xml);
                    } else if (parsedData.status === "error") {
                        generatingProcesses.value[targetProcessId] = false;
                        reject(new Error(parsedData.detail ?? "Unknown error"));
                    }
                } catch {
                    // Not a regular message with parseable content.
                    toast.add({
                        severity: "error",
                        summary: `Got invalid information to continue XML generation.`,
                    });
                    generatingProcesses.value[targetProcessId] = false;
                    resolve(undefined);
                }
            });

            eventSource.addEventListener("close", () => {
                // Stream ended naturally; resolve as no-op if not already settled.
                resolve(undefined);
            });

            eventSource.addEventListener("error", (error) => {
                if (error.code) {
                    // HTTP 4xx/5xx or other real server error — report and reject.
                    generatingProcesses.value[targetProcessId] = false;
                    toast.add({
                        severity: "error",
                        summary: `Could not complete BPMN generation`,
                        detail: `${error.code}: ${error.message}`,
                    });
                    reject(error);
                } else {
                    // Spurious reconnect/connection-drop event from the eventsource
                    // package — could be navigation away or stream end. Do NOT clear
                    // the generating flag here; polling will clear it once the backend
                    // confirms generation is done.
                    resolve(undefined);
                }
            });
        }).finally(() => {
            eventSource.close();
        });
    };

    // No isFetching short-circuit here: edits made WHILE a refetch is in
    // flight must read as modified, or the landing-time clobber guard below
    // can't see them and the refetch's response overwrites the user's text.
    const isModified = computed(() => {
        return Boolean(
            isNameModified.value ||
            description.value?.isModified ||
            definition.value?.isModified
        );
    });

    // One shared opts object: the notification guards below and the
    // landing-time commit gates inside the update functions must agree on
    // what "would clobber local changes".
    const externalRefresh = {
        entityType: EntityType.PROCESS,
        currentEntityId: () => process.value?.identity,
        isDirty: () => isModified.value,
        refetch: () => updateProcess(true),
    };

    refreshOnExternalEntityChange(externalRefresh);

    refreshOnCrossTabEntitySync({
        ...externalRefresh,
        enabled: () => !!stek.value,
    });

    const queueSave = async () => {
        if (!isModified.value || !process.value) {
            return;
        }

        const updateContent: types.StartProcessProcessMgtUpdateProcessStartPostData =
            {
                body: {
                    process_id: process.value.identity,
                },
                url: "/process/process_mgt/update_process/start",
            };

        if (isNameModified.value) {
            updateContent.body.name = process.value.name;
        }

        if (definition.value?.isModified) {
            updateContent.body.process_definition = definition.value.blob;
        }
        if (description.value?.isModified) {
            updateContent.body.description = description.value.blob;
        }

        if (
            !updateContent.body.process_id ||
            Object.keys(updateContent.body).length === 1
        ) {
            // Only process_id is set, no need to update.
            return;
        }

        syncStore.queueSync({
            entityType: EntityType.PROCESS,
            args: [updateContent],
        });

        if (definition.value?.isModified) {
            definition.value.persist();
        }
        if (description.value?.isModified) {
            description.value.persist();
        }
        if (isNameModified.value) {
            persistName();
        }
    };

    /**
     * Bypasses the inactivity debounce to immediately sync the current BPMN
     * process definition to the backend. Used when a new shape is added to the
     * diagram so the new element becomes navigatable near-instantly.
     */
    const triggerImmediateSync = async () => {
        await queueSave();
        if (process.value?.identity) {
            await syncStore.sync(process.value.identity);
        }
    };

    const reset = async () => {
        await description.value?.resetContent();
        await definition.value?.resetContent();
        resetName();
    };

    const { isEditing } = useEditManager();
    const throttledSave = throttle(queueSave, FILE_SYNC_THROTTLE_MS);
    registerFlush(() => throttledSave.flush());

    watch([isEditing, isModified], ([_, modified]) => {
        if (modified) {
            throttledSave();
        }
    });

    return {
        process,
        description,
        definition,
        generateProcessDefinitionXml,
        updateDefinition,
        updateProcess,
        isGeneratingBpmn,
        isAnyGenerating,
        isFetching: computed(() =>
            Boolean(
                isFetchingProcess.value ||
                isFetchingDefinition.value ||
                isFetchingDescription.value
            )
        ),
        isModified,
        reset,
        triggerImmediateSync,
    };
});

export const useProcessManager = () => storeToRefs(useProcessStore());
